import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { colors, fonts, gradients, radius, shadows } from "@/lib/theme";

interface Props {
    onGenerate: () => void;
    /**
     * True once generation has run and produced nothing. The screen can then be
     * completed, because "this visit has no teaching to give" is an answer.
     * False means nobody has generated yet.
     */
    settled?: boolean;
}

export function TeachingEmptyState({ onGenerate, settled }: Props) {
    return (
        <View style={styles.container}>
            <LinearGradient
                colors={[...gradients.surface]}
                style={styles.iconContainer}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
            >
                <MaterialCommunityIcons
                    name="book-open-page-variant-outline"
                    size={44}
                    color={colors.teal400}
                />
            </LinearGradient>

            <Text style={styles.title}>
                {settled ? "No teaching for this visit" : "No teaching yet"}
            </Text>

            <Text style={styles.subtitle}>
                Our AI reads the assessment and writes teaching for the patient and caregiver —
                matched to the findings, section by section.
            </Text>

            <TouchableOpacity onPress={onGenerate} activeOpacity={0.85} style={styles.btnWrapper}>
                <LinearGradient
                    colors={[...gradients.brand]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.generateBtn}
                >
                    <MaterialCommunityIcons name="star-four-points" size={20} color="#FFFFFF" />
                    <Text style={styles.generateBtnText}>Generate Teaching</Text>
                </LinearGradient>
            </TouchableOpacity>

            <View style={styles.noteRow}>
                <MaterialCommunityIcons
                    name="information-outline"
                    size={14}
                    color={colors.textTertiary}
                    style={{ opacity: 0.6 }}
                />
                <Text style={styles.noteText}>Takes about 20-40 seconds</Text>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 32,
        paddingVertical: 40
    },
    iconContainer: {
        width: 96,
        height: 96,
        borderRadius: radius["2xl"],
        borderWidth: 1,
        borderColor: colors.border,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 24,
        ...shadows.sm
    },
    title: {
        fontSize: 20,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        marginBottom: 10
    },
    subtitle: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        textAlign: "center",
        lineHeight: 21,
        maxWidth: 290,
        marginBottom: 32
    },
    btnWrapper: {
        width: "100%",
        maxWidth: 280
    },
    generateBtn: {
        width: "100%",
        paddingVertical: 16,
        paddingHorizontal: 24,
        borderRadius: radius.lg,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        ...shadows.brand
    },
    generateBtnText: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    noteRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginTop: 16
    },
    noteText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary
    }
});
